import type { EnumRegistryRole } from "@shared/contracts/enumRegistry";

type RoleCollection = EnumRegistryRole[] | Map<string, EnumRegistryRole>;

export interface RoleOption {
  value: string;
  label: string;
}

function toTitleCase(input: string): string {
  return input
    .toLowerCase()
    .split(" ")
    .map((token) =>
      token.length > 0 ? token[0].toUpperCase() + token.slice(1) : token,
    )
    .join(" ");
}

export function normalizeRoleCode(role: string | null | undefined): string {
  return String(role || "")
    .trim()
    .toUpperCase();
}

export function formatRoleLabel(role: string | null | undefined): string {
  const normalized = normalizeRoleCode(role);
  if (!normalized) return "Unknown";
  return toTitleCase(normalized.replace(/_/g, " "));
}

export function buildRoleLookupMap(
  roles: EnumRegistryRole[],
): Map<string, EnumRegistryRole> {
  const map = new Map<string, EnumRegistryRole>();
  for (const role of roles) {
    const code = normalizeRoleCode(role.code);
    if (code) map.set(code, role);
  }
  return map;
}

function roleMapFromCollection(
  roles: RoleCollection,
): Map<string, EnumRegistryRole> {
  return roles instanceof Map ? roles : buildRoleLookupMap(roles);
}

export function getRoleLookup(
  role: string | null | undefined,
  roles: RoleCollection,
): EnumRegistryRole | null {
  const code = normalizeRoleCode(role);
  if (!code) return null;
  return roleMapFromCollection(roles).get(code) || null;
}

export function getRoleLabel(
  role: string | null | undefined,
  roles: RoleCollection,
): string {
  const roleLookup = getRoleLookup(role, roles);
  if (!roleLookup) return formatRoleLabel(role);
  return formatRoleLabel(roleLookup.code);
}

export function isPrivilegedRole(
  role: string | null | undefined,
  roles: RoleCollection,
): boolean {
  const code = normalizeRoleCode(role);
  if (!code) return false;

  const roleLookup = getRoleLookup(code, roles);
  if (!roleLookup) return false;

  return Boolean(
    roleLookup.capabilities.isPrivilegedLogin ||
    roleLookup.capabilities.canManageOrgAppointments ||
    roleLookup.capabilities.canManageOrgEegConfig ||
    roleLookup.capabilities.canAccessMedicalCoding,
  );
}

export function isAdminLikeRole(
  role: string | null | undefined,
  roles: RoleCollection,
): boolean {
  const code = normalizeRoleCode(role);
  if (!code) return false;

  const roleLookup = getRoleLookup(code, roles);
  if (!roleLookup) return false;

  return Boolean(
    roleLookup.capabilities.canManageOrgAppointments ||
    roleLookup.capabilities.canManageOrgEegConfig ||
    roleLookup.capabilities.canAccessMedicalCoding ||
    (roleLookup.isSystem &&
      roleLookup.capabilities.patientAccessScope === "all" &&
      roleLookup.capabilities.isPrivilegedLogin),
  );
}

export function isSuperAdminLikeRole(
  role: string | null | undefined,
  roles: RoleCollection,
): boolean {
  const code = normalizeRoleCode(role);
  if (!code) return false;

  const roleLookup = getRoleLookup(code, roles);
  if (!roleLookup) return false;

  return Boolean(
    roleLookup.isSystem &&
    roleLookup.capabilities.patientAccessScope === "all" &&
    roleLookup.capabilities.isPrivilegedLogin,
  );
}

export function canAccessCommercialBilling(
  role: string | null | undefined,
  roles: RoleCollection,
): boolean {
  const code = normalizeRoleCode(role);
  if (!code) return false;

  if (["SUPER_ADMIN", "ORG_ADMIN", "ADMIN"].includes(code)) {
    return true;
  }

  return isSuperAdminLikeRole(code, roles);
}

export function canReadCommercialStateFromRegistry(
  role: string | null | undefined,
  organizationId: string | null | undefined,
  roles: RoleCollection,
): boolean {
  if (!organizationId) return false;
  return canAccessCommercialBilling(role, roles);
}

export function canSignReports(
  user:
    | {
        role?: string | null;
        permissions?: string[];
        capabilities?: { canSignReports?: boolean };
      }
    | null
    | undefined,
): boolean {
  if (!user) return false;
  if (user.capabilities?.canSignReports === true) return true;
  if (Array.isArray(user.permissions) && user.permissions.includes("reports:sign")) {
    return true;
  }
  return normalizeRoleCode(user.role) === "PHYSICIAN";
}

export function getRoleBadgeVariant(
  role: string | null | undefined,
  roles: RoleCollection,
): string {
  const code = normalizeRoleCode(role);
  const roleLookup = getRoleLookup(code, roles);

  if (!roleLookup) return "secondary";
  if (isSuperAdminLikeRole(code, roles)) return "danger";
  if (isAdminLikeRole(code, roles)) return "warning";
  if (roleLookup?.capabilities.requiresDeidentification) return "dark";

  switch (roleLookup?.capabilities.patientAccessScope) {
    case "department":
      return "primary";
    case "assigned":
      return "info";
    case "all":
      return "danger";
    default:
      return "secondary";
  }
}

export function buildRoleSelectOptions(
  roles: EnumRegistryRole[],
): RoleOption[] {
  return roles
    .map((role) => ({
      value: normalizeRoleCode(role.code),
      label: formatRoleLabel(role.code),
    }))
    .filter((option) => Boolean(option.value))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Roles that qualify as clinical providers in appointment scheduling.
 * Must stay in sync with CLINICIAN_PROVIDER_ROLES in appointmentController.ts.
 */
export const CLINICAL_PROVIDER_ROLE_CODES = new Set([
  "PHYSICIAN",
  "TECHNICIAN",
  "NURSE",
  "CLINICIAN_READONLY",
]);

export function isClinicalProviderRole(
  role: string | null | undefined,
): boolean {
  return CLINICAL_PROVIDER_ROLE_CODES.has(normalizeRoleCode(role));
}

/** Human-readable hints shown under the role select for clinical roles. */
const CLINICAL_ROLE_HINTS: Record<string, string> = {
  TECHNICIAN:
    "EEG Technologist — primary role for EEG upload, recording management and study creation. Most commonly assigned provider.",
  PHYSICIAN:
    "Physician / Neurologist — full clinical access; can order and interpret studies.",
  NURSE:
    "Nurse / Nurse Practitioner — patient access and study management; cannot sign reports.",
  CLINICIAN_READONLY:
    "Read-only clinician — can view studies and recordings; no write access.",
};

export function getClinicalRoleHint(
  role: string | null | undefined,
): string | null {
  return CLINICAL_ROLE_HINTS[normalizeRoleCode(role)] ?? null;
}

export interface GroupedRoleOptions {
  clinical: RoleOption[];
  other: RoleOption[];
}

/**
 * Returns roles split into two groups: clinical providers first, then all others.
 * TECHNICIAN is sorted first within the clinical group.
 */
export function buildGroupedRoleSelectOptions(
  roles: EnumRegistryRole[],
): GroupedRoleOptions {
  const all = buildRoleSelectOptions(roles);
  const clinical = all
    .filter((o) => isClinicalProviderRole(o.value))
    .sort((a, b) => {
      // TECHNICIAN always first in the clinical group
      if (a.value === "TECHNICIAN") return -1;
      if (b.value === "TECHNICIAN") return 1;
      return a.label.localeCompare(b.label);
    });
  const other = all.filter((o) => !isClinicalProviderRole(o.value));
  return { clinical, other };
}
